// Хелперы отображения шага «Перевозка»: остатки/проценты, дружелюбные лейблы документов по типу,
// сбор дневных УПД, подсчёт ТС по этапам, документы по дням, дефолтный таб. Чистые функции (без React).
import { fmtFwdDate } from '@/lib/forwarding';
import { TONS_PER_VEHICLE } from '@/lib/logistics';
import type { BargainDocument, BargainStepsInfo, ForwardingInfo, SignatureRole } from '@/types/deals';

type Translate = (key: string, options?: Record<string, unknown>) => string;
type DocType = NonNullable<BargainDocument['documentType']>;

/** Ключ весового этапа перевозки (= value таба этапа). */
export type ForwardingStageKey = 'allocated' | 'loaded' | 'ongoing' | 'unloaded';

export function remaining(total: number | undefined, done: number | undefined): number {
  return Math.max(0, (total ?? 0) - (done ?? 0));
}

export function percent(done: number | undefined, total: number | undefined): number {
  if (!total || total <= 0) {
    return 0;
  }
  return Math.min(100, Math.round(((done ?? 0) / total) * 100));
}

/** Дневная мощность отгрузки в тоннах: «X авто/сут» (type='TS') → X×30 т; «X т/сут» → X т. */
export function dailyCapacityTon(
  rate: { readonly type?: string; readonly value?: number } | undefined,
): number | undefined {
  if (rate?.value == null) {
    return undefined;
  }
  return rate.type === 'TS' ? rate.value * TONS_PER_VEHICLE : rate.value;
}

// Тип документа → явный i18n-ключ лейбла (без динамической сборки ключей — таблица литералов).
const DOC_LABEL_KEY: Partial<Record<DocType, string>> = {
  FORWARDER_RECEIPT: 'app.deals.forwarding.docReceipt',
  REGISTER_OF_ACCEPTED: 'app.deals.forwarding.docRegisterAccepted',
  DAILY_REF_FOR_GOODS_QUALITY: 'app.deals.forwarding.docQualityRef',
  FORWARDER_TTN: 'app.deals.forwarding.docTtn',
  FORWARDER_TRN: 'app.deals.forwarding.docTrn',
  TRUNCATED_RTS: 'app.deals.forwarding.docRts',
  FORWARDER_REPORT: 'app.deals.forwarding.docForwarderReport',
};

const DAILY_UPD_TYPES = new Set<DocType>(['SIGNED_DAILY_UPD', 'DAILY_UPD_TEMPLATE']);

export function isDailyUpd(doc: BargainDocument): boolean {
  return doc.documentType != null && DAILY_UPD_TYPES.has(doc.documentType);
}

/** Контекстный лейбл документа перевозки (УПД за дату / Реестр принятого / …) или имя файла. */
export function forwardingDocLabel(doc: BargainDocument, t: Translate): string {
  const type = doc.documentType;
  if (type && DAILY_UPD_TYPES.has(type)) {
    const date = fmtFwdDate(doc.deliveryDate);
    return date ? t('app.deals.forwarding.docDailyUpd', { date }) : t('app.deals.forwarding.docDailyUpdNoDate');
  }
  // РТС — показываем полное имя файла (напр. «2026-002-008 РТС 2026-06-18.pdf»): это реестр ТС за день,
  // имя несёт № сделки + дату; fallback на обобщённый лейбл, если имени нет.
  if (type === 'TRUNCATED_RTS') {
    return doc.name ?? t('app.deals.forwarding.docRts');
  }
  const key = type ? DOC_LABEL_KEY[type] : undefined;
  return key ? t(key) : (doc.name ?? '');
}

/** Дневные УПД сделки — для секции «Подпись УПД». УПД формируется приёмной стороной по дню (из
 *  реестра принятого) и подписывается через ЭДО — это документ СДЕЛКИ, поэтому берём из общего
 *  списка `documents`, а не из дней отгрузки. Дедуп по id, иначе по дате+имени (без id — без скачивания). */
export function collectDailyUpds(documents: readonly BargainDocument[] | undefined): BargainDocument[] {
  const seen = new Set<string>();
  const out: BargainDocument[] = [];
  for (const doc of documents ?? []) {
    if (!isDailyUpd(doc)) {
      continue;
    }
    const key = doc.id != null ? `id:${doc.id}` : `dn:${doc.deliveryDate ?? ''}|${doc.name ?? ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(doc);
    }
  }
  return out;
}

export function countVehiclesAllocated(fwd: ForwardingInfo | undefined): number {
  return (fwd?.plannedDeliveryInfo?.deliveryDays ?? []).reduce((n, d) => n + (d.numVehicles ?? 0), 0);
}

export function countVehiclesLoaded(fwd: ForwardingInfo | undefined): number {
  return (fwd?.loadedDeliveryInfo?.loadingDays ?? []).reduce((n, d) => n + (d.vehicles?.length ?? 0), 0);
}

export function countVehiclesUnloaded(fwd: ForwardingInfo | undefined): number {
  return (fwd?.unloadedDeliveryInfo?.unloadedDays ?? []).reduce((n, d) => n + (d.vehicles?.length ?? 0), 0);
}

// Документы уровня дня (без УПД — он в «Подпись УПД»; без ТТН/ТРН — они в карточке ТС).
const DAY_DOC_TYPES = new Set<DocType>([
  'TRUNCATED_RTS',
  'FORWARDER_RECEIPT',
  'REGISTER_OF_SHIPPED',
  'REGISTER_OF_ACCEPTED',
  'DAILY_REF_FOR_GOODS_QUALITY',
  'FORWARDER_REPORT',
]);

export interface ForwardingDocsDay {
  /** ISO-дата дня (yyyy-mm-dd) или undefined для документов без даты. */
  readonly date: string | undefined;
  readonly docs: BargainDocument[];
}

/** Документы перевозки, сгруппированные по дню (для вкладки «Документы») — по убыванию даты. */
export function collectDocsByDay(documents: readonly BargainDocument[] | undefined): ForwardingDocsDay[] {
  const byDate = new Map<string, BargainDocument[]>();
  for (const doc of documents ?? []) {
    if (!doc.documentType || !DAY_DOC_TYPES.has(doc.documentType)) {
      continue;
    }
    const key = (doc.deliveryDate ?? '').slice(0, 10);
    const arr = byDate.get(key) ?? [];
    arr.push(doc);
    byDate.set(key, arr);
  }
  return [...byDate.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, docs]) => ({ date: date || undefined, docs }));
}

export interface VehicleDocs {
  /** Гос. номер ТС (+ прицеп) — заголовок карточки. */
  readonly regnum: string;
  readonly ttnId?: string;
  /** ISO-дата погрузки (для подписи). */
  readonly date?: string;
  /** Документы машины: ТТН, ТРН (по факту погрузки). */
  readonly docs: BargainDocument[];
}

// Документы уровня машины — только ТТН/ТРН. Allow-list (как DAY_DOC_TYPES для дней): если бэк
// прицепит к ТС документ другого типа (напр. дневной УПД), он не должен протечь в «По машинам».
const VEHICLE_DOC_TYPES = new Set<DocType>(['FORWARDER_TTN', 'FORWARDER_TRN']);

/** Машины с ТТН/ТРН по факту погрузки — для секции «По машинам» вкладки «Документы». Источник —
 *  отгруженные ТС по дням (ForwardingLoadedVehicleInfo несёт documents и ttnId). Машины без ТТН/ТРН
 *  пропускаем (док-ты формируются после завершения периода погрузки). */
export function collectVehicleDocs(info: BargainStepsInfo): VehicleDocs[] {
  const out: VehicleDocs[] = [];
  for (const day of info.forwardingInfo?.loadedDeliveryInfo?.loadingDays ?? []) {
    for (const v of day.vehicles ?? []) {
      const docs = (v.documents ?? []).filter((d) => d.documentType != null && VEHICLE_DOC_TYPES.has(d.documentType));
      if (docs.length === 0) {
        continue;
      }
      const regnum = [v.registrationNumber, v.trailerRegistrationNumber].filter(Boolean).join(' / ');
      out.push({ regnum: regnum || (v.ttnId ?? '—'), ttnId: v.ttnId, date: day.loadingDate, docs });
    }
  }
  return out;
}

const USER_ROLE_TO_SIGN: Readonly<Record<string, SignatureRole>> = {
  CUSTOMER: 'BUYER',
  AGENT: 'AGENT',
  SELLER: 'SELLER',
  BANK: 'BANK',
};

/** Сколько дневных УПД ждут подписи ТЕКУЩЕГО пользователя (по его роли). 0 — действий нет. */
export function pendingUpdCount(info: BargainStepsInfo): number {
  const role = info.userRole ? USER_ROLE_TO_SIGN[info.userRole] : undefined;
  if (!role) {
    return 0;
  }
  return collectDailyUpds(info.documents).filter((doc) => doc.signatures?.[role]?.state === 'WAITED').length;
}

/** Дефолтный таб этапа — самый продвинутый этап с данными (Доставлено > В пути > Отгружено > Разобрано). */
export function defaultForwardingTab(fwd: ForwardingInfo | undefined): ForwardingStageKey {
  const unloaded = fwd?.unloadedDeliveryInfo;
  const ongoing = fwd?.ongoingDeliveryInfo;
  const loaded = fwd?.loadedDeliveryInfo;
  if (unloaded?.unloadedDays?.length || unloaded?.unloadedWeightKg) {
    return 'unloaded';
  }
  if (ongoing?.vehicles?.length || ongoing?.ongoingWeightKg) {
    return 'ongoing';
  }
  if (loaded?.loadingDays?.length || loaded?.loadedWeightKg) {
    return 'loaded';
  }
  return 'allocated';
}
